import React, { useState } from 'react'
import { Plus, Search, Edit, Trash2, Calendar, User, Truck, AlertTriangle } from 'lucide-react'
import { useData } from '../context/DataContext'
import VehicleForm from '../components/VehicleForm'
import Modal from '../components/Modal'
import DeleteConfirmModal from '../components/DeleteConfirmModal'

// Helper function to check if a date is expiring within one week
const isExpiringSoon = (expiryDate) => {
  if (!expiryDate) return false
  
  const today = new Date()
  const expiry = new Date(expiryDate)
  const oneWeekFromNow = new Date(today.getTime() + (7 * 24 * 60 * 60 * 1000))
  
  return expiry <= oneWeekFromNow && expiry >= today
}

// Helper function to check if a date is already expired
const isExpired = (expiryDate) => {
  if (!expiryDate) return false
  
  const today = new Date()
  const expiry = new Date(expiryDate)
  
  return expiry < today
}

function Vehicles() {
  const { state, addVehicle, updateVehicle, deleteVehicle } = useData()
  const { vehicles, customers, loading } = state

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingVehicle, setEditingVehicle] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')

  const [deleteModal, setDeleteModal] = useState({ isOpen: false, vehicle: null })

  const filteredVehicles = vehicles.filter(vehicle => {
    const matchesSearch = vehicle.registrationNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.make.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
              (vehicle.nextServiceKm && vehicle.nextServiceKm.toString().includes(searchTerm))
    
    return matchesSearch
  })

  const handleAddVehicle = () => {
    setEditingVehicle(null)
    setIsModalOpen(true)
  }

  const handleEditVehicle = (vehicle) => {
    setEditingVehicle(vehicle)
    setIsModalOpen(true)
  }

  const handleDeleteVehicle = (vehicle) => {
    if (vehicle.is_assigned) {
      alert(`Cannot delete vehicle ${vehicle.registrationNumber} as it is currently assigned to a customer. Please unassign it first.`)
      return
    }
    
    deleteVehicle(vehicle.id)
  }

  const openDeleteModal = (vehicle) => {
    if (vehicle.is_assigned) {
      alert(`Cannot delete vehicle ${vehicle.registrationNumber} as it is currently assigned to a customer. Please unassign it first.`)
      return
    }
    setDeleteModal({ isOpen: true, vehicle })
  }

  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, vehicle: null })
  }

  const confirmDelete = () => {
    if (deleteModal.vehicle) {
      handleDeleteVehicle(deleteModal.vehicle)
    }
  }

  const handleSubmit = async (vehicleData) => {
    try {
      if (editingVehicle) {
        const result = await updateVehicle(editingVehicle.id, vehicleData)
        if (result.success) {
          setIsModalOpen(false)
          setEditingVehicle(null)
        } else {
          alert(`Error updating vehicle: ${result.error}`)
        }
      } else {
        const result = await addVehicle(vehicleData)
        if (result.success) {
          setIsModalOpen(false)
          setEditingVehicle(null)
        } else {
          alert(`Error creating vehicle: ${result.error}`)
        }
      }
    } catch (error) {
      console.error('Error submitting vehicle:', error)
      alert('An error occurred while saving the vehicle. Please try again.')
    }
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setEditingVehicle(null)
  }

  // Show loading state during operations
  const isUpdating = loading && editingVehicle
  const isCreating = loading && !editingVehicle && isModalOpen



  return (
    <div className="container mx-auto px-4 py-2">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Vehicle Management</h1>
          <p className="text-gray-600">Manage your fleet and vehicle information</p>
        </div>
        <div className="mt-4 sm:mt-0 flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleAddVehicle}
            className="btn btn-primary flex items-center space-x-2"
          >
            <Plus className="h-5 w-5" />
            <span>Add Vehicle</span>
          </button>
        </div>
      </div>

      {/* Total Vehicles Card */}
      <div className="mb-6">
        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <Truck className="h-4 w-4 sm:h-5 sm:w-5 text-red-600" />
            </div>
            <div className="ml-2 sm:ml-3">
              <p className="text-sm font-medium text-gray-600">Total Vehicles</p>
              <p className="text-base sm:text-lg font-semibold text-gray-900">{vehicles.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4 space-y-4 my-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search vehicles by registration, type, make, or model..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input pl-10"
            />
          </div>
          
        </div>
      </div>

      {/* Vehicle List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredVehicles.length > 0 ? (
          filteredVehicles.map((vehicle) => (
            <div key={vehicle.id} className="card p-6 hover:shadow-md transition-shadow duration-200">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{vehicle.registrationNumber}</h3>
                    {vehicle.is_assigned && (
                      <div className="flex items-center space-x-1 bg-brand-red bg-opacity-10 px-2 py-1 rounded-full">
                        <User className="h-3 w-3 text-brand-red" />
                        <span className="text-xs font-medium text-brand-red">ASSIGNED</span>
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{vehicle.make} {vehicle.model}</p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEditVehicle(vehicle)}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                    title="Edit Vehicle"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  {/* Delete button hidden */}
                  {/* <button
                    onClick={() => openDeleteModal(vehicle)}
                    className={`p-2 rounded-lg transition-colors duration-200 ${
                      vehicle.is_assigned 
                        ? 'text-gray-300 cursor-not-allowed' 
                        : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                    }`}
                    title={vehicle.is_assigned ? 'Cannot delete assigned vehicle' : 'Delete Vehicle'}
                    disabled={vehicle.is_assigned}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button> */}
                </div>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Type:</span>
                  <span className="font-medium text-gray-900">{vehicle.type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Year:</span>
                  <span className="font-medium text-gray-900">{vehicle.year}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Fuel Type:</span>
                  <span className="font-medium text-gray-900">{vehicle.fuelType}</span>
                </div>
                {vehicle.nextServiceKm && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Next Service:</span>
                    <span className="font-medium text-gray-900">{parseFloat(vehicle.nextServiceKm).toLocaleString()} km</span>
                  </div>
                )}
                {vehicle.capacity && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Capacity:</span>
                    <span className="font-medium text-gray-900">{vehicle.capacity}</span>
                  </div>
                )}
                {vehicle.is_assigned && vehicle.assigned_to && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Assigned to:</span>
                    <span className="font-medium text-brand-red">
                      {customers.find(c => c.id === vehicle.assigned_to)?.name || 'Unknown Customer'}
                    </span>
                  </div>
                )}

              </div>

              {/* Insurance & Documents */}
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="space-y-2 text-sm">
                  {vehicle.insuranceExpiry && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="text-gray-500">Insurance Expiry:</span>
                        {isExpired(vehicle.insuranceExpiry) && (
                          <div className="flex items-center space-x-1">
                            <AlertTriangle className="h-3 w-3 text-red-500" />
                            <span className="text-xs text-red-600 font-medium">EXPIRED</span>
                          </div>
                        )}
                        {isExpiringSoon(vehicle.insuranceExpiry) && !isExpired(vehicle.insuranceExpiry) && (
                          <div className="flex items-center space-x-1">
                            <AlertTriangle className="h-3 w-3 text-orange-500" />
                            <span className="text-xs text-orange-600 font-medium">EXPIRING SOON</span>
                          </div>
                        )}
                      </div>
                      <span className={`text-xs ${isExpired(vehicle.insuranceExpiry) ? 'text-red-600 font-medium' : isExpiringSoon(vehicle.insuranceExpiry) ? 'text-orange-600 font-medium' : 'text-gray-600'}`}>
                        {new Date(vehicle.insuranceExpiry).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  {vehicle.permitExpiry && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="text-gray-500">Permit Expiry:</span>
                        {isExpired(vehicle.permitExpiry) && (
                          <div className="flex items-center space-x-1">
                            <AlertTriangle className="h-3 w-3 text-red-500" />
                            <span className="text-xs text-red-600 font-medium">EXPIRED</span>
                          </div>
                        )}
                        {isExpiringSoon(vehicle.permitExpiry) && !isExpired(vehicle.permitExpiry) && (
                          <div className="flex items-center space-x-1">
                            <AlertTriangle className="h-3 w-3 text-orange-500" />
                            <span className="text-xs text-orange-600 font-medium">EXPIRING SOON</span>
                          </div>
                        )}
                      </div>
                      <span className={`text-xs ${isExpired(vehicle.permitExpiry) ? 'text-red-600 font-medium' : isExpiringSoon(vehicle.permitExpiry) ? 'text-orange-600 font-medium' : 'text-gray-600'}`}>
                        {new Date(vehicle.permitExpiry).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  {vehicle.fitnessExpiry && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="text-gray-500">Fitness Expiry:</span>
                        {isExpired(vehicle.fitnessExpiry) && (
                          <div className="flex items-center space-x-1">
                            <AlertTriangle className="h-3 w-3 text-red-500" />
                            <span className="text-xs text-red-600 font-medium">EXPIRED</span>
                          </div>
                        )}
                        {isExpiringSoon(vehicle.fitnessExpiry) && !isExpired(vehicle.fitnessExpiry) && (
                          <div className="flex items-center space-x-1">
                            <AlertTriangle className="h-3 w-3 text-orange-500" />
                            <span className="text-xs text-orange-600 font-medium">EXPIRING SOON</span>
                          </div>
                        )}
                      </div>
                      <span className={`text-xs ${isExpired(vehicle.fitnessExpiry) ? 'text-red-600 font-medium' : isExpiringSoon(vehicle.fitnessExpiry) ? 'text-orange-600 font-medium' : 'text-gray-600'}`}>
                        {new Date(vehicle.fitnessExpiry).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  {vehicle.pollutionExpiry && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="text-gray-500">Pollution Expiry:</span>
                        {isExpired(vehicle.pollutionExpiry) && (
                          <div className="flex items-center space-x-1">
                            <AlertTriangle className="h-3 w-3 text-red-500" />
                            <span className="text-xs text-red-600 font-medium">EXPIRED</span>
                          </div>
                        )}
                        {isExpiringSoon(vehicle.pollutionExpiry) && !isExpired(vehicle.pollutionExpiry) && (
                          <div className="flex items-center space-x-1">
                            <AlertTriangle className="h-3 w-3 text-orange-500" />
                            <span className="text-xs text-orange-600 font-medium">EXPIRING SOON</span>
                          </div>
                        )}
                      </div>
                      <span className={`text-xs ${isExpired(vehicle.pollutionExpiry) ? 'text-red-600 font-medium' : isExpiringSoon(vehicle.pollutionExpiry) ? 'text-orange-600 font-medium' : 'text-gray-600'}`}>
                        {new Date(vehicle.pollutionExpiry).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  {vehicle.roadTaxExpiry && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="text-gray-500">Road Tax Expiry:</span>
                        {isExpired(vehicle.roadTaxExpiry) && (
                          <div className="flex items-center space-x-1">
                            <AlertTriangle className="h-3 w-3 text-red-500" />
                            <span className="text-xs text-red-600 font-medium">EXPIRED</span>
                          </div>
                        )}
                        {isExpiringSoon(vehicle.roadTaxExpiry) && !isExpired(vehicle.roadTaxExpiry) && (
                          <div className="flex items-center space-x-1">
                            <AlertTriangle className="h-3 w-3 text-orange-500" />
                            <span className="text-xs text-orange-600 font-medium">EXPIRING SOON</span>
                          </div>
                        )}
                      </div>
                      <span className={`text-xs ${isExpired(vehicle.roadTaxExpiry) ? 'text-red-600 font-medium' : isExpiringSoon(vehicle.roadTaxExpiry) ? 'text-orange-600 font-medium' : 'text-gray-600'}`}>
                        {new Date(vehicle.roadTaxExpiry).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {vehicle.notes && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <p className="text-sm text-gray-600 line-clamp-2">{vehicle.notes}</p>
                </div>
              )}

              <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-400">
                Added: {new Date(vehicle.createdAt).toLocaleDateString()}
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full">
            <div className="text-center py-12">
              <div className="bg-gray-100 rounded-full p-3 w-16 h-16 mx-auto mb-4">
                <Plus className="h-10 w-10 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No vehicles found</h3>
              <p className="text-gray-500 mb-4">
                {searchTerm
                  ? 'Try adjusting your search criteria' 
                  : 'Get started by adding your first vehicle'
                }
              </p>
              {!searchTerm && (
                <button
                  onClick={handleAddVehicle}
                  className="btn btn-primary"
                >
                  Add Your First Vehicle
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Vehicle Form Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingVehicle ? 'Edit Vehicle' : 'Add New Vehicle'}
        size="max-w-4xl"
      >
        <VehicleForm
          vehicle={editingVehicle}
          onSubmit={handleSubmit}
          onCancel={handleCloseModal}
          isSubmitting={isUpdating || isCreating}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        title="Delete Vehicle"
        message={`Are you sure you want to delete vehicle "${deleteModal.vehicle?.registrationNumber}"?`}
        itemName={deleteModal.vehicle?.registrationNumber || 'Vehicle'}
      />
    </div>
  )
}

export default Vehicles
